import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { CreateDinoDuoInput } from '@/types/dinoDuos';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BookSelector } from '@/components/BookSelector';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, ArrowRight, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface CreateDinoDuoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateDuo: (input: CreateDinoDuoInput) => Promise<void>;
  preselectedBookId?: string;
}

type Step = 'book' | 'friends' | 'details';

export function CreateDinoDuoDialog({
  open,
  onOpenChange,
  onCreateDuo,
  preselectedBookId
}: CreateDinoDuoDialogProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('book');
  const [selectedBook, setSelectedBook] = useState<any>(null);
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [creating, setCreating] = useState(false);

  // Fetch user's friends
  const { data: friends } = useQuery({
    queryKey: ['friends', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get followers and following
      const [{ data: followers }, { data: following }] = await Promise.all([
        supabase
          .from('follows')
          .select('follower_id, profiles:follower_id(id, username, name, image)')
          .eq('following_id', user.id)
          .eq('status', 'accepted'),
        supabase
          .from('follows')
          .select('following_id, profiles:following_id(id, username, name, image)')
          .eq('follower_id', user.id)
          .eq('status', 'accepted')
      ]);

      const allFriends = new Map();
      
      followers?.forEach((f: any) => {
        const profile = Array.isArray(f.profiles) ? f.profiles[0] : f.profiles;
        if (profile) allFriends.set(profile.id, profile);
      });
      
      following?.forEach((f: any) => {
        const profile = Array.isArray(f.profiles) ? f.profiles[0] : f.profiles;
        if (profile) allFriends.set(profile.id, profile);
      });

      return Array.from(allFriends.values());
    },
    enabled: open && !!user
  });

  const handleBookSelect = (book: any) => {
    setSelectedBook(book);
    setStep('friends');
  };

  const toggleFriend = (friendId: string) => {
    setSelectedFriends(prev =>
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : prev.length < 9
        ? [...prev, friendId]
        : prev
    );
  };

  const handleFriendsNext = () => {
    setStep('details');
  };

  const handleCreate = async () => {
    if (!selectedBook || creating) return;

    setCreating(true);
    try {
      await onCreateDuo({
        book_id: selectedBook.id,
        title: title || undefined,
        start_date: startDate?.toISOString(),
        target_end_date: endDate?.toISOString(),
        member_ids: selectedFriends
      });

      // Reset form
      setStep('book');
      setSelectedBook(null);
      setSelectedFriends([]);
      setTitle('');
      setStartDate(undefined);
      setEndDate(undefined);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating duo:', error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'book' && '📚 Choose a Book'}
            {step === 'friends' && '👥 Invite Friends (up to 9)'}
            {step === 'details' && '✨ Duo Details'}
          </DialogTitle>
        </DialogHeader>

        {step === 'book' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Search for a book to read together with your friends
            </p>
            <BookSelector
              onSelect={handleBookSelect}
              open={true}
              onClose={() => onOpenChange(false)}
            />
          </div>
        )}

        {step === 'friends' && selectedBook && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              <img
                src={selectedBook.cover_url || '/placeholder.svg'}
                alt={selectedBook.title}
                className="w-12 h-18 object-cover rounded"
              />
              <div>
                <p className="font-medium">{selectedBook.title}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedBook.authors?.join(', ')}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Selected: {selectedFriends.length}/9 friends
              </p>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {friends?.map((friend: any) => (
                    <div
                      key={friend.id}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
                      onClick={() => toggleFriend(friend.id)}
                    >
                      <Checkbox
                        checked={selectedFriends.includes(friend.id)}
                        disabled={!selectedFriends.includes(friend.id) && selectedFriends.length >= 9}
                      />
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={friend.image} />
                        <AvatarFallback>
                          {friend.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{friend.name || friend.username}</p>
                        <p className="text-sm text-muted-foreground">@{friend.username}</p>
                      </div>
                    </div>
                  ))}
                  {(!friends || friends.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No friends found. Follow some users to invite them to your Duo!
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('book')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleFriendsNext}
                className="flex-1"
                disabled={selectedFriends.length === 0}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 'details' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Duo Name (Optional)</Label>
              <Input
                id="title"
                placeholder="e.g., Summer Reading Squad"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>Target End Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !endDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('friends')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleCreate} className="flex-1" disabled={creating}>
                {creating ? 'Creating...' : 'Create Dino Duo 🦕'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
